import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { DOC_TYPE, fmtDate, SUBJECT_TO_COMPLIANCE, getSubjectToPackage } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Upload, FileText, Lock, ShieldCheck, FolderCheck, MapPin, Printer, PenLine } from "lucide-react";
import { Input } from "@/components/ui/input";
import { printFilledForm } from "@/lib/printForm";

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ document_type: "Purchase Agreement", confidential: false, signature_required: false });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [esignDoc, setEsignDoc] = useState(null);
  const fileRef = useRef();
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const load = () => api.get("/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); api.get("/leads").then((r) => setLeads(r.data)); }, []);

  const upload = async () => {
    if (!file) return toast.error("Choose a file");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = (await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
      const l = leads.find((x) => x.id === f.lead_id);
      await api.post("/documents", { ...f, property_id: l?.property_id, document_name: f.document_name || up.original_filename, storage_path: up.storage_path, original_filename: up.original_filename, content_type: up.content_type, upload_date: new Date().toISOString() });
      toast.success("Document uploaded"); setOpen(false); setFile(null); setF({ document_type: "Purchase Agreement", confidential: false, signature_required: false }); load();
    } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Document Center" subtitle={`${docs.length} documents stored`} action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="upload-doc-btn"><Upload className="w-4 h-4 mr-1" /> Upload</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle className="font-display">Upload Document</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">File</Label>
                <input ref={fileRef} type="file" onChange={(e) => setFile(e.target.files[0])} data-testid="doc-file" className="block w-full text-sm border border-input rounded-md p-2" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Document Type</Label>
                <Select value={f.document_type} onValueChange={set("document_type")}><SelectTrigger data-testid="doc-type"><SelectValue /></SelectTrigger><SelectContent>{DOC_TYPE.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Link to Lead</Label>
                <Select value={f.lead_id} onValueChange={set("lead_id")}><SelectTrigger data-testid="doc-lead"><SelectValue placeholder="Optional" /></SelectTrigger><SelectContent>{leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.lead_id_display} · {l.property?.property_address}</SelectItem>)}</SelectContent></Select></div>
              <div className="flex items-center gap-3"><Switch checked={f.confidential} onCheckedChange={set("confidential")} data-testid="doc-confidential" /><Label>Confidential</Label></div>
              <div className="flex items-center gap-3"><Switch checked={f.signature_required} onCheckedChange={set("signature_required")} data-testid="doc-sig" /><Label>Signature Required</Label></div>
            </div>
            <DialogFooter><Button onClick={upload} disabled={busy} data-testid="doc-submit">{busy ? "Uploading…" : "Upload"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>} />
      <div className="p-4 sm:p-6 lg:p-8">
        <Tabs defaultValue="all">
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all-docs">All Documents ({docs.length})</TabsTrigger>
            <TabsTrigger value="signatures" data-testid="tab-signatures">Signatures</TabsTrigger>
            <TabsTrigger value="subjectto" data-testid="tab-subject-to">Subject-To Compliance</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-5">
            {docs.length === 0 ? <div className="text-muted-foreground bg-card border border-border rounded-md p-10 text-center">No documents uploaded yet.</div> :
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {docs.map((d) => (
                  <div key={d.id} className="bg-card border border-border rounded-md p-5 hover:-translate-y-1 hover:shadow-sm transition-transform duration-200" data-testid={`doc-card-${d.id}`}>
                    <div className="flex items-start justify-between"><FileText className="w-8 h-8 text-accent" />{d.confidential && <Lock className="w-4 h-4 text-destructive" />}</div>
                    <div className="font-medium mt-3 truncate">{d.document_name || d.original_filename}</div>
                    <div className="text-xs text-muted-foreground mt-1">{d.document_type} · {fmtDate(d.upload_date)}</div>
                    {d.content_html && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        <Button size="sm" variant="outline" onClick={() => printFilledForm(d.document_name || "Document", d.content_html)} data-testid={`view-doc-${d.id}`}><Printer className="w-3.5 h-3.5 mr-1" /> View / Print</Button>
                        <Button size="sm" variant="outline" onClick={() => setEsignDoc(d)} data-testid={`esign-doc-${d.id}`}><PenLine className="w-3.5 h-3.5 mr-1" /> Request Signature</Button>
                        {d.signatures?.length > 0 && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">✓ Signed ×{d.signatures.length}</Badge>}
                      </div>
                    )}
                    {d.signature_required && <Badge variant="outline" className="mt-3 bg-amber-100 text-amber-800">Signature Required</Badge>}
                  </div>
                ))}
              </div>}
          </TabsContent>

          <TabsContent value="signatures" className="mt-5">
            <SignaturesDashboard />
          </TabsContent>

          <TabsContent value="subjectto" className="mt-5">
            <SubjectToCompliance leads={leads} />
          </TabsContent>
        </Tabs>
      </div>
      {esignDoc && <ESignDialog doc={esignDoc} onClose={() => { setEsignDoc(null); load(); }} />}
    </div>
  );
}

function SubjectToCompliance({ leads }) {
  const [leadId, setLeadId] = useState("");
  const [lead, setLead] = useState(null);
  const [compliance, setCompliance] = useState({});
  const [pkg, setPkg] = useState({});

  const onSelect = async (id) => {
    setLeadId(id);
    const { data } = await api.get(`/leads/${id}`);
    setLead(data);
    setCompliance(data.subject_to_compliance || {});
    setPkg(data.subject_to_package || {});
  };

  const toggleCompliance = async (item) => {
    const next = { ...compliance, [item]: !compliance[item] };
    setCompliance(next);
    try { await api.put(`/leads/${leadId}`, { subject_to_compliance: next }); }
    catch { setCompliance(compliance); toast.error("Could not save — please try again"); }
  };
  const togglePkg = async (item) => {
    const next = { ...pkg, [item]: !pkg[item] };
    setPkg(next);
    try { await api.put(`/leads/${leadId}`, { subject_to_package: next }); }
    catch { setPkg(pkg); toast.error("Could not save — please try again"); }
  };

  const state = lead?.property?.state || "";
  const pack = getSubjectToPackage(state);
  const done = SUBJECT_TO_COMPLIANCE.filter((c) => compliance[c]).length;
  const pct = Math.round((done / SUBJECT_TO_COMPLIANCE.length) * 100);
  const pkgDone = pack.docs.filter((d) => pkg[d]).length;

  return (
    <div className="max-w-4xl">
      <div className="bg-card border border-border rounded-md p-5 mb-6">
        <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium mb-2">Select Subject-To Deal</div>
        <Select value={leadId} onValueChange={onSelect}>
          <SelectTrigger data-testid="subto-lead-select"><SelectValue placeholder="Choose a deal" /></SelectTrigger>
          <SelectContent>{leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.lead_id_display} · {l.owner?.first_name} {l.owner?.last_name} · {l.property?.property_address}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!leadId ? <div className="text-muted-foreground bg-card border border-border rounded-md p-10 text-center">Select a deal to view its Subject-To compliance checklist and state document package.</div> : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-card border border-border rounded-md" data-testid="subto-compliance-panel">
            <div className="px-5 py-3 border-b border-border font-display font-bold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-accent" /> Compliance Checklist</div>
            <div className="p-5">
              <div className="flex items-center justify-between mb-2 text-sm"><span className="text-muted-foreground">Progress</span><span className="font-mono-nums">{done}/{SUBJECT_TO_COMPLIANCE.length} · {pct}%</span></div>
              <Progress value={pct} className="mb-5" />
              <div className="space-y-1">
                {SUBJECT_TO_COMPLIANCE.map((item, idx) => (
                  <label key={item} className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-secondary cursor-pointer transition-colors" data-testid={`subto-compliance-${idx}`}>
                    <Checkbox checked={!!compliance[item]} onCheckedChange={() => toggleCompliance(item)} />
                    <span className={`text-sm ${compliance[item] ? "line-through text-muted-foreground" : ""}`}>{item}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-md h-fit" data-testid="subto-package-panel">
            <div className="px-5 py-3 border-b border-border font-display font-bold flex items-center gap-2"><FolderCheck className="w-4 h-4 text-accent" /> {pack.label}</div>
            <div className="p-5">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4"><MapPin className="w-3.5 h-3.5" /> {state ? `Package for ${state}` : "No property state — using default package"} · {pkgDone}/{pack.docs.length} prepared</div>
              <div className="space-y-1">
                {pack.docs.map((d, idx) => (
                  <label key={d} className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-secondary cursor-pointer transition-colors" data-testid={`subto-package-${idx}`}>
                    <Checkbox checked={!!pkg[d]} onCheckedChange={() => togglePkg(d)} />
                    <span className={`text-sm ${pkg[d] ? "line-through text-muted-foreground" : ""}`}>{d}</span>
                  </label>
                ))}
              </div>
              <div className="mt-4 flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2">
                <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Subject-To deals carry due-on-sale and title risk. Complete every item and retain signed originals before closing.</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SignaturesDashboard() {
  const [reqs, setReqs] = useState([]);
  const [filter, setFilter] = useState("pending");
  useEffect(() => { api.get("/esign/all").then((r) => setReqs(r.data)).catch(() => {}); }, []);
  const shown = reqs.filter((r) => filter === "all" || r.status === filter);
  const pending = reqs.filter((r) => r.status === "pending").length;
  return (
    <div className="bg-card border border-border rounded-md" data-testid="signatures-dashboard">
      <div className="px-5 py-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
        <div className="font-display font-bold">Signature Requests <span className="text-muted-foreground font-normal text-sm">({pending} pending)</span></div>
        <div className="flex gap-1">
          {["pending", "signed", "all"].map((x) => (
            <Button key={x} size="sm" variant={filter === x ? "default" : "ghost"} onClick={() => setFilter(x)} className="capitalize" data-testid={`sig-filter-${x}`}>{x}</Button>
          ))}
        </div>
      </div>
      {shown.length === 0 ? (
        <p className="p-8 text-center text-sm text-muted-foreground" data-testid="signatures-empty">No {filter !== "all" ? filter : ""} signature requests.</p>
      ) : (
        <div className="divide-y divide-border">
          {shown.map((r) => (
            <div key={r.id} className="px-5 py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`sig-row-${r.id}`}>
              <div className="min-w-0">
                <div className="font-medium truncate">{r.document_name}</div>
                <div className="text-xs text-muted-foreground">{r.signer_name} ({r.signer_role}) · {r.signer_email} · sent {fmtDate(r.created_at)}{r.reminder_count > 0 && ` · ${r.reminder_count} reminder${r.reminder_count > 1 ? "s" : ""}`}</div>
              </div>
              <div className="flex items-center gap-2">
                {r.status === "pending" && r.url && (
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(r.url); toast.success("Signing link copied"); }} data-testid={`sig-copy-${r.id}`}>Copy link</Button>
                )}
                <Badge className={r.status === "signed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
                  {r.status === "signed" ? `Signed ${fmtDate(r.signed_at)}` : `Pending · expires ${fmtDate(r.expires_at)}`}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ESignDialog({ doc, onClose }) {
  const [requests, setRequests] = useState([]);
  const [f, setF] = useState({ signer_name: "", signer_email: "", signer_role: "Seller" });
  const [busy, setBusy] = useState(false);
  const [lastUrl, setLastUrl] = useState("");
  const loadReqs = () => api.get(`/esign/requests?document_id=${doc.id}`).then((r) => setRequests(r.data)).catch(() => {});
  useEffect(() => { loadReqs(); }, []); // eslint-disable-line

  const send = async () => {
    if (!f.signer_name.trim() || !f.signer_email.includes("@")) { toast.error("Signer name and a valid email are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/esign/requests", { document_id: doc.id, ...f });
      setLastUrl(data.url);
      toast.success(data.email_sent ? `Signing link emailed to ${f.signer_email}` : "Link created — copy it below (email not configured)");
      setF({ signer_name: "", signer_email: "", signer_role: "Seller" });
      loadReqs();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create signing link"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Request Signature — {doc.document_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Signer name</Label>
              <Input value={f.signer_name} onChange={(e) => setF({ ...f, signer_name: e.target.value })} data-testid="esign-name" /></div>
            <div className="space-y-1"><Label className="text-xs">Role</Label>
              <Select value={f.signer_role} onValueChange={(v) => setF({ ...f, signer_role: v })}>
                <SelectTrigger data-testid="esign-role"><SelectValue /></SelectTrigger>
                <SelectContent>{["Seller", "Buyer", "Assignee", "Lender", "Partner", "Witness"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Signer email</Label>
            <Input type="email" value={f.signer_email} onChange={(e) => setF({ ...f, signer_email: e.target.value })} data-testid="esign-email" /></div>
          <Button onClick={send} disabled={busy} className="w-full" data-testid="esign-send">{busy ? "Creating…" : "Email signing link"}</Button>
          {lastUrl && (
            <div className="text-xs bg-secondary rounded-md p-3" data-testid="esign-url">
              <div className="break-all">{lastUrl}</div>
              <Button size="sm" variant="outline" className="mt-2 h-7" onClick={() => { navigator.clipboard.writeText(lastUrl); toast.success("Link copied"); }}>Copy link</Button>
            </div>
          )}
          {requests.length > 0 && (
            <div className="border-t border-border pt-3">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Signature requests</div>
              {requests.map((r) => (
                <div key={r.id} className="flex items-center justify-between text-sm py-1" data-testid={`esign-req-${r.id}`}>
                  <span>{r.signer_name} <span className="text-muted-foreground">({r.signer_role})</span></span>
                  <Badge className={r.status === "signed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
                    {r.status === "signed" ? `Signed ${fmtDate(r.signed_at)}` : "Pending"}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
